import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import StockTicker from '../components/layout/StockTicker';
import Footer from '../components/layout/Footer';
import { ArticleCardStandard } from '../components/cards/ArticleCard';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(query);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query) {
      searchArticles(query);
    }
  }, [query]);

  const searchArticles = async (q) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/articles/search/${encodeURIComponent(q)}`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error searching articles:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ q: searchQuery });
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="search-page">
      <StockTicker />
      <Navbar />

      {/* Header */}
      <header className="py-12 border-b-2 border-black">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h1
            className="text-4xl md:text-6xl font-black text-black mb-6 tracking-tight leading-[0.95]"
            style={{ fontFamily: "'Merriweather', serif" }}
          >
            Search
          </h1>

          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" strokeWidth={2} />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for articles..."
                className="pl-12 py-6 text-lg bg-white border-2 border-black focus:border-red-600 focus:ring-0 text-black rounded-none"
                data-testid="search-page-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-black text-white hover:bg-red-600 font-mono font-bold uppercase tracking-widest px-8 rounded-none text-xs border-0"
              data-testid="search-page-submit"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
            </Button>
          </form>
        </div>
      </header>

      {/* Results */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {query && (
            <p className="text-zinc-500 mb-8 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              {loading ? (
                'Searching...'
              ) : (
                <>
                  Found <span className="text-black font-bold">{articles.length}</span> results for "
                  <span className="text-red-600 font-bold">{query}</span>"
                </>
              )}
            </p>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="w-full h-[300px]" />
              ))}
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article) => (
                <ArticleCardStandard key={article.id} article={article} />
              ))}
            </div>
          ) : query ? (
            <div className="text-center py-20">
              <p className="text-zinc-600 mb-4 font-bold" style={{ fontFamily: "'DM Sans', sans-serif" }}>No articles found for "{query}"</p>
              <p className="text-zinc-400 text-sm font-mono uppercase tracking-wider">Try different keywords or browse our categories</p>
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-zinc-400 font-mono text-sm uppercase tracking-wider">Enter a search term to find articles</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
